/**
 * ASPSP (bank) list hook (Phase 2, §7.5).
 *
 * GET /api/aspsps is proxied from EB `GET /aspsps` and cached ~1h server-side
 * per (application_id, country, psu_type); the client mirrors that with a 1h
 * `staleTime`. Requires a stored credential — otherwise the API answers 409
 * `eb_not_configured`, so callers gate the query behind `useProviderCredential`.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk } from '../queryKeys.ts';
import type { AspspOut } from '../types.ts';

export interface UseAspspsOptions {
  /** Skip the query until a credential exists (avoids the 409). */
  enabled?: boolean;
  /** Cache lifetime; defaults to 1h to match the server-side ASPSP cache. */
  staleTime?: number;
}

/** The list of supported banks for a country (default DE, personal PSUs). */
export function useAspsps(
  country = 'DE',
  options: UseAspspsOptions = {},
): UseQueryResult<AspspOut[]> {
  return useQuery({
    queryKey: qk.aspsps(country),
    queryFn: () =>
      api.get<AspspOut[]>(
        `/api/aspsps?country=${encodeURIComponent(country)}&psu_type=personal`,
      ),
    staleTime: options.staleTime ?? 3600_000,
    enabled: options.enabled ?? true,
  });
}
